import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Send, Check } from 'lucide-react';
import { PulseBeams } from "@/components/ui/pulse-beams";
import { Link } from 'react-router-dom';

const beams = [
  {
    path: "M269 220.5H16.5C10.9772 220.5 6.5 224.977 6.5 230.5V398.5",
    gradientConfig: {
      initial: {
        x1: "0%",
        x2: "0%",
        y1: "80%",
        y2: "100%",
      },
      animate: {
        x1: ["0%", "0%", "200%"],
        x2: ["0%", "0%", "180%"],
        y1: ["80%", "0%", "0%"],
        y2: ["100%", "20%", "20%"],
      },
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "loop",
        ease: "linear",
        repeatDelay: 2,
        delay: Math.random() * 2,
      },
    },
    connectionPoints: [
      { cx: 6.5, cy: 398.5, r: 6 },
      { cx: 269, cy: 220.5, r: 6 }
    ]
  },
  {
    path: "M568 200H841C846.523 200 851 195.523 851 190V40",
    gradientConfig: {
      initial: {
        x1: "0%",
        x2: "0%",
        y1: "80%",
        y2: "100%",
      },
      animate: {
        x1: ["20%", "100%", "100%"],
        x2: ["0%", "90%", "90%"],
        y1: ["80%", "80%", "-20%"],
        y2: ["100%", "100%", "0%"],
      },
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "loop",
        ease: "linear",
        repeatDelay: 2,
        delay: Math.random() * 2,
      },
    },
    connectionPoints: [
      { cx: 851, cy: 34, r: 6.5 },
      { cx: 568, cy: 200, r: 6 }
    ]
  },
  {
    path: "M425.5 274V333C425.5 338.523 421.023 343 415.5 343H152C146.477 343 142 347.477 142 353V426.5",
    gradientConfig: {
      initial: {
        x1: "0%",
        x2: "0%",
        y1: "80%",
        y2: "100%",
      },
      animate: {
        x1: ["20%", "100%", "100%"],
        x2: ["0%", "90%", "90%"],
        y1: ["80%", "80%", "-20%"],
        y2: ["100%", "100%", "0%"],
      },
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "loop",
        ease: "linear",
        repeatDelay: 2,
        delay: Math.random() * 2,
      },
    },
    connectionPoints: [
      { cx: 142, cy: 427, r: 6.5 },
      { cx: 425.5, cy: 274, r: 6 }
    ]
  }
];

const gradientColors = {
  start: "#18CCFC",
  middle: "#6344F5",
  end: "#AE48FF"
};

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Njord AI Automation Agency",
    "description": "Get in touch with Njord for custom AI automation solutions. Book a demo or discuss your business automation needs.",
    "mainEntity": {
      "@type": "Organization",
      "name": "Njord AI Automation Agency",
      "telephone": "+447737031147",
      "email": "lennon@njordai.co.uk",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "2 Saxon Park, Saxon Way East",
        "addressLocality": "Corby",
        "postalCode": "NN18 9EY",
        "addressCountry": "GB"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+447737031147",
        "contactType": "customer service",
        "email": "lennon@njordai.co.uk",
        "availableLanguage": "English",
        "areaServed": "GB"
      }
    }
  };

  return (
    <>
      <Helmet>
        {/* Contact Schema */}
        <script type="application/ld+json">
          {JSON.stringify(contactSchema)}
        </script>
      </Helmet>
      
      <section id="contact" className="relative bg-black py-20">
        <PulseBeams
          beams={beams}
          gradientColors={gradientColors}
          className="absolute inset-0"
        />
        
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="container max-w-6xl px-4 py-16 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              {/* Contact Info */}
              <div className="hidden lg:block bg-black/10 backdrop-blur-sm rounded-lg p-6 md:p-8 border border-white/10 hover:bg-black/20 transition-colors">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">Connect With Us</h2>
                
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Mail className="h-6 w-6 text-primary-300" />
                    </div>
                    <a 
                      href="mailto:lennon@njordai.co.uk" 
                      className="text-lg text-white hover:text-primary-300 transition-colors"
                    >
                      lennon@njordai.co.uk
                    </a>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Phone className="h-6 w-6 text-primary-300" />
                    </div>
                    <a 
                      href="tel:+447737031147" 
                      className="text-lg text-white hover:text-primary-300 transition-colors"
                    >
                      +44 7737 031147
                    </a>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-primary-300" />
                    </div>
                    <address className="text-lg text-white not-italic leading-relaxed">
                      2 Saxon Park<br />
                      Saxon Way East<br />
                      NN18 9EY
                    </address>
                  </div>
                </div>

                {/* Add internal links to improve SEO */}
                  <div className="hidden lg:block mt-8 pt-6 border-t border-white/10">
                  <h3 className="text-lg font-semibold text-white mb-4">Our Services</h3>
                  <div className="flex flex-col gap-2">
                    <Link
                      to="/ai-consultation"
                      className="text-primary-300 hover:text-primary-200 text-sm transition-colors"
                    >
                      AI Consulting
                    </Link>
                    <Link
                      to="/geo"
                      className="text-primary-300 hover:text-primary-200 text-sm transition-colors"
                    >
                      GEO
                    </Link>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-black/10 backdrop-blur-sm rounded-lg p-6 md:p-8 border border-white/10 hover:bg-black/20 transition-colors lg:col-span-1 col-span-1">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Get Started</h2>
                {isSubmitted ? (
                  <div className="bg-green-50/10 backdrop-blur border border-green-200/20 rounded-lg p-6 text-center">
                    <div className="mx-auto w-12 h-12 flex items-center justify-center bg-green-100/20 rounded-full mb-4">
                      <Check className="h-6 w-6 text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-green-400 mb-2">Message Sent!</h3>
                    <p className="text-green-300">
                      Thanks for reaching out. We'll be in touch with you shortly to discuss your automation needs.
                    </p>
                  </div>
                ) : (
                  <form
                    action="https://formspree.io/f/movdgnvn"
                    method="POST"
                    onSubmit={(e) => {
                      e.preventDefault();
                      setIsSubmitting(true);
                      const form = e.target as HTMLFormElement;
                      fetch(form.action, {
                        method: 'POST',
                        body: new FormData(form),
                        headers: {
                          'Accept': 'application/json'
                        }
                      }).then(response => {
                        if (response.ok) {
                          setIsSubmitted(true);
                        }
                      }).finally(() => {
                        setIsSubmitting(false);
                      });
                    }}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-white/80 mb-1">
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-md focus:ring-2 focus:ring-primary focus:border-primary text-white"
                          placeholder="John Smith"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-1">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-md focus:ring-2 focus:ring-primary focus:border-primary text-white"
                          placeholder="john@company.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-white/80 mb-1">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-md focus:ring-2 focus:ring-primary focus:border-primary text-white"
                        placeholder="Your Company Ltd."
                      />
                    </div>

                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-white/80 mb-1">
                        Service Interest
                      </label>
                      <select
                        id="service"
                        name="service"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-md focus:ring-2 focus:ring-primary focus:border-primary text-white"
                      >
                        <option value="ai-consulting">AI Consulting</option>
                        <option value="geo">GEO (Generative Engine Optimisation)</option>
                        <option value="not-sure">Not Sure Yet</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-white/80 mb-1">
                        Your Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-md focus:ring-2 focus:ring-primary focus:border-primary text-white"
                        placeholder="Tell us a bit about your automation needs..."
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary hover:bg-primary-600 text-white py-3 px-6 rounded-md transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center">
                          <div className="animate-spin mr-2 h-5 w-5 text-white">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </div>
                          Sending...
                        </div>
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Additional spacing to prevent footer overlap */}
      <div className="bg-black h-20"></div>
    </>
  );
};

export default Contact;